import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import sanityClient from '../../config/sanityClient';
import { PortableText } from '@portabletext/react';

// Firebase Imports (same as BlogPage)
import { logEvent } from 'firebase/analytics';
import { analytics, auth } from '../../firebase/firebase';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail
} from 'firebase/auth';

// Component Imports
import AuthModal from '../auth/AuthModal';

// Icon Imports
import { Menu, X } from 'lucide-react';

const googleProvider = new GoogleAuthProvider();

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  // ----- Header / Nav state (same as BlogPage) -----
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navLinks = [
    { href: '/#features', label: 'Features' },
    { href: '/#how-to-use', label: 'How It Works' },
    { href: '/#pricing', label: 'Pricing' },
    { href: '/how-to-use', label: 'User Guide' },
    { href: '/blog', label: 'Blog' },
    { href: '/#faq', label: 'FAQ' },
  ];

  // ----- Auth modal state & handlers (same as BlogPage) -----
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tptStoreUrlInput, setTptStoreUrlInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');

  const openAuthModal = (mode = 'signup') => {
    setAuthError('');
    setResetMessage('');
    setShowForgotPassword(false);
    setAuthMode(mode);
    setIsAuthModalOpen(true);
  };

  const handleAuthAction = async (mode) => {
    setAuthError('');
    try {
      if (mode === 'signup') {
        await createUserWithEmailAndPassword(auth, email, password);
        logEvent(analytics, 'sign_up', { method: 'Email' });
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        logEvent(analytics, 'login', { method: 'Email' });
      }
      setIsAuthModalOpen(false);
      navigate('/app');
    } catch (error) {
      setAuthError(error.message.replace('Firebase: ', ''));
    }
  };

  const handleGoogleSignIn = async () => {
    setAuthError('');
    try {
      await signInWithPopup(auth, googleProvider);
      logEvent(analytics, 'sign_up', { method: 'Google' });
      setIsAuthModalOpen(false);
      navigate('/app');
    } catch (error) {
      setAuthError(error.message.replace('Firebase: ', ''));
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setAuthError('');
    setResetMessage('');
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage('Password reset email sent! Please check your inbox.');
    } catch (error) {
      setAuthError(error.message.replace('Firebase: ', ''));
    }
  };

  // ----- Fetch single post -----
  const [post, setPost] = useState(null);
  const [loadingPost, setLoadingPost] = useState(true);
  const [postError, setPostError] = useState('');

  useEffect(() => {
    const query = `
      *[_type == "post" && slug.current == $slug][0]{
        title,
        "slug": slug.current,
        publishedAt,
        excerpt,
        mainImage{asset->{url, metadata{lqip}}},
        body
      }
    `;
    sanityClient
      .fetch(query, { slug })
      .then((data) => setPost(data || null))
      .catch((err) => {
        console.error('Sanity single post error:', err);
        const msg =
          err?.response?.body?.message ||
          err?.message ||
          'Unknown error contacting Sanity';
        setPostError(msg);
      })
      .finally(() => setLoadingPost(false));
  }, [slug]);

  const pageTitle = post?.title || 'Blog Post';
  const ogImage = post?.mainImage?.asset?.url || '/blog-og.jpg';
  const description =
    post?.excerpt || 'Read TPT SEO and sales tips from Mr. Joel’s TPT Tools.';

  return (
    <>
      <Helmet>
        <title>{pageTitle} | TPT Seller Blog</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={`${pageTitle} | TPT Seller Blog`} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={`https://mrjoelstptgenerator.com/blog/${slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${pageTitle} | TPT Seller Blog`} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>

      <div className="bg-slate-50 text-slate-900 min-h-screen flex flex-col">
        {/* ----- Header (identical to BlogPage) ----- */}
        <header className="bg-white/80 backdrop-blur-md sticky top-0 z-50 border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex-shrink-0">
                <Link to="/" className="text-xl font-bold text-sky-800">Mr Joel's Seller Tools</Link>
              </div>
              <nav className="hidden md:flex items-center space-x-2">
                {navLinks.map((link) =>
                  link.href === '/blog' ? (
                    <Link key={link.href} to={link.href} className="font-bold text-sky-600 bg-sky-100 transition-colors px-3 py-2 rounded-md">
                      {link.label}
                    </Link>
                  ) : (
                    <a key={link.href} href={link.href} className="font-medium text-slate-600 hover:text-sky-600 transition-colors px-3 py-2 rounded-md">
                      {link.label}
                    </a>
                  )
                )}
                <button
                  onClick={() => openAuthModal('signup')}
                  className="bg-sky-600 hover:bg-sky-700 text-white font-bold py-2 px-4 rounded-full text-sm shadow-lg transition-transform transform hover:scale-105"
                >
                  Sign Up Free
                </button>
                <a
                  href="/app"
                  className="text-slate-600 hover:text-sky-600 font-medium py-2 px-4 rounded-full text-sm"
                >
                  Login
                </a>
              </nav>
              <div className="md:hidden flex items-center">
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="inline-flex items-center justify-center p-2 rounded-md text-slate-700"
                  aria-expanded={isMenuOpen}
                >
                  {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </div>
            </div>
          </div>
          {isMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                {navLinks.map((link) =>
                  link.href === '/blog' ? (
                    <Link key={link.href} to={link.href} onClick={() => setIsMenuOpen(false)} className="font-bold text-sky-600 bg-sky-100 block px-3 py-2 rounded-md text-base">
                      {link.label}
                    </Link>
                  ) : (
                    <a key={link.href} href={link.href} onClick={() => setIsMenuOpen(false)} className="text-slate-600 hover:bg-sky-50 hover:text-sky-700 block px-3 py-2 rounded-md text-base font-medium">
                      {link.label}
                    </a>
                  )
                )}
                <button onClick={() => { openAuthModal('signup'); setIsMenuOpen(false); }} className="w-full bg-sky-100 text-sky-700 block px-3 py-3 rounded-md text-base font-bold text-center mt-4">
                  Sign Up for Free
                </button>
                <a href="/app" className="w-full text-center block py-2 text-slate-600 font-medium">
                  Login
                </a>
              </div>
            </div>
          )}
        </header>

        {/* ----- Main (single post) ----- */}
        <main className="flex-grow py-12 sm:py-16 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            {loadingPost && <p className="text-center text-slate-500">Loading…</p>}
            {!loadingPost && postError && (
              <p className="text-center text-red-600">Error: {postError}</p>
            )}
            {!loadingPost && !post && !postError && (
              <div className="text-center">
                <h1 className="text-2xl font-bold text-slate-800">Post not found</h1>
                <p className="mt-3 text-slate-600">
                  The post you’re looking for doesn’t exist.{' '}
                  <Link to="/blog" className="text-sky-700 underline">Back to blog</Link>.
                </p>
              </div>
            )}

            {!loadingPost && post && (
              <article className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                {post?.mainImage?.asset?.url && (
                  <img
                    src={post.mainImage.asset.url}
                    alt={post.title}
                    className="w-full h-64 object-cover"
                  />
                )}
                <div className="p-6 sm:p-8">
                  {/* Match card styles */}
                  <h1 className="text-2xl font-semibold text-sky-700 hover:underline">
                    {post.title}
                  </h1>
                  {post.publishedAt && (
                    <p className="text-sm text-slate-500 mt-2">
                      Published on {new Date(post.publishedAt).toLocaleDateString()}
                    </p>
                  )}

                  {post.excerpt && (
                    <p className="mt-5 text-lg leading-relaxed text-slate-700">
                      {post.excerpt}
                    </p>
                  )}

                  {/* Enhanced typography for body content */}
                  <div className="prose prose-slate lg:prose-lg max-w-none mt-8">
                    <PortableText value={post.body} />
                  </div>

                  <div className="mt-10">
                    <Link
                      to="/blog"
                      className="inline-block text-sky-700 font-medium hover:underline"
                    >
                      ← Back to all posts
                    </Link>
                  </div>
                </div>
              </article>
            )}
          </div>
        </main>

        {/* ----- Footer (styled buttons to avoid a11y warnings) ----- */}
        <footer className="mt-16 bg-slate-800 text-slate-400 py-12">
          <div className="max-w-5xl mx-auto text-center px-4">
            <p className="text-sm sm:text-base">&copy; 2025 Mr. Joel's Seller Tools. All Rights Reserved.</p>
            <div className="mt-4 flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={() => {}}
                className="text-slate-400 hover:text-white text-sm sm:text-base underline underline-offset-4"
              >
                Contact
              </button>
              <button
                type="button"
                onClick={() => {}}
                className="text-slate-400 hover:text-white text-sm sm:text-base underline underline-offset-4"
              >
                Privacy Policy
              </button>
              <button
                type="button"
                onClick={() => {}}
                className="text-slate-400 hover:text-white text-sm sm:text-base underline underline-offset-4"
              >
                Terms of Service
              </button>
            </div>
          </div>
        </footer>
      </div>

      {/* ----- AuthModal (identical props) ----- */}
      {isAuthModalOpen && (
        <AuthModal
          onClose={() => setIsAuthModalOpen(false)}
          authMode={authMode}
          setAuthMode={setAuthMode}
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          authError={authError}
          setAuthError={setAuthError}
          showForgotPassword={showForgotPassword}
          setShowForgotPassword={setShowForgotPassword}
          resetEmail={resetEmail}
          setResetEmail={setResetEmail}
          resetMessage={resetMessage}
          setResetMessage={setResetMessage}
          tptStoreUrlInput={tptStoreUrlInput}
          setTptStoreUrlInput={setTptStoreUrlInput}
          handleAuthAction={handleAuthAction}
          handlePasswordReset={handlePasswordReset}
          handleGoogleSignIn={handleGoogleSignIn}
        />
      )}
    </>
  );
};

export default BlogPost;
